import { useState, useEffect, useRef } from 'react';
import { Outlet, useLoaderData, useNavigate, useRouteLoaderData, useParams, Navigate, useSubmit } from 'react-router';
import { Header, CircularMenuButton, Scrollable, useIsFormSubmitting, useHandleSubmitError, Actionbar, ActionbarLeft, ActionbarRight, useIsReloading, InlineHeader, InlineSection } from '@/components/DashboardComponents'
import { MessageItem } from '@/pages/SessionLayout';
import { FormProvider, FormSubmitProvider, FormObjectGroup } from '@/form/FormProvider';
import { Button, ComboButton, Spacing, Textbox, TextboxGroup, Dropdown, DropdownOption, Checkbox, RadioGroup, RadioOption } from '@/form/FormComponents';
import { GenericDropdown } from '@/components/GenericDropdown';
import { Icon } from '@/components/Icon';
import { GenericEmptyListPlaceholder } from '@/DashboardLayout';
import { timeSince, timeTaken, formatDate } from '@/shared/utils';
import { SaveTranscriptButton, DiagnosesAndDifferentialsForm } from '@/pages/SessionLayout';
import { regenerateFeedback, returnSubmissions } from '@/data/GraderData'
import { useErrorModal, useModal, Modal, ModalContent, ModalButtons } from '@/components/Modal';
import { useMenu } from '@/components/Menu';
import { useContinueUnlessBlocked, useShouldBlock } from '@/systems/Blocker';
import { useIsDeveloper, usePrivacyMode } from '@/systems/Authentication';
import Lottie from "lottie-react";
import sparklesAnimation from '@/../assets/icons8-sparkles.json';
import { useDatabaseSync } from '@/systems/DatabaseSync';
import { useDate } from '@/hooks/useDate';
import { useIsMobile, useBreakpoint } from '@/hooks/useIsMobile';

function StudentDropdownItem({ student, due }) {
  const privacyMode = usePrivacyMode();

  return (
    <div className="student_dropdown_item">
      <div><StudentCompletionIcon session={student.session} due={due}/></div>
      <div>{privacyMode ? "[Student name]" : (student.name || "(No name)")}</div>
    </div>
  )
}

function PlaceholderDropdownItem() {
  return (
    <div className="student_dropdown_item">
      <div></div>
      <div>{"Select student..."}</div>
    </div>
  )
}

function StudentCompletionIcon({ session, due }) {
  return session ? (
    session.submitted ? (
      session.returned ? (
        (due && due < session.submitted_date) ? (
          <Icon name="check-clock" style={{fill: "var(--submission_graded)"}}/>
        ) : (
          <Icon name="check-circle" style={{fill: "var(--submission_graded)"}}/>
        )
      ) : (
        (due && due < session.submitted_date) ? (
          <Icon name="check-outline-clock" style={{fill: "var(--submission_graded)"}}/>
        ) : (
          <Icon name="check-circle-outline" style={{fill: "var(--submission_graded)"}}/>
        )
      )
    ) : (
      <Icon name="progress-clock" style={{fill: "var(--submission_in_progress)"}}/>
    )
  ) : (
    <Icon name="dotted-circle" style={{fill: "var(--submission_not_started)"}}/>
  );
}

export function GraderLayoutFrame() {
  const navigate = useNavigate();
  const continueUnlessBlocked = useContinueUnlessBlocked();
  
  const grader = useLoaderData().data.value;
  const assessment = grader.assessment;
  const params = useParams();

  const just_students = useDatabaseSync({ query: {collection: "course_memberships", course_id: assessment.course_id}, original_value: grader.students, enabled: true }).filter(a => !a.role || a.role == "student").map(a => {
    const session = grader.students.find(b => b._id == a._id)?.session;
    return {...a, session: session || a.session};
  });
  const sessions = useDatabaseSync({ query: {collection: "sessions", assessment_id: assessment._id}, original_value: [], enabled: true });

  const students = just_students.map(a => {
    const session = sessions.find(b => b.uid == a._id);
    return {...a, session: session || a.session};
  });

  const this_student_has_session = students.find(a => a._id == params.student_uid)?.session;
  const prev_session = useRef(this_student_has_session);
  const prev_student = useRef(params.student_uid);
  useEffect(() => {
    if (this_student_has_session && !prev_session.current && params.student_uid === prev_student.current) {
      navigate(params.student_uid, {replace: true});
    }
    prev_session.current = this_student_has_session;
    prev_student.current = params.student_uid;
  }, [this_student_has_session, navigate, params.student_uid]);

  const items = students.map(a => {return {name: a._id, element: <StudentDropdownItem student={a} due={assessment.data.due_date}/>, height: 40}});
  const selected_student_id = students.find(a => a._id == params.student_uid)?._id;
  const selected_student_index = students.findIndex(a => a._id == selected_student_id);
  const selected_student = students.find(a => a._id == selected_student_id);

  const handleChange = (value) => {
    if (value !== selected_student_id) {
      continueUnlessBlocked(() => {
        navigate(value, {replace: true});
      });
    }
  }

  const hasPrevious = selected_student_index > 0;

  const handlePrevious = () => {
    const index = selected_student_index;
    if (index > 0) {
      navigate(students[index-1]._id, {replace: true});
    }
  }

  const hasNext = selected_student_index < students.length-1;

  const handleNext = () => {
    const index = selected_student_index;
    if (index < students.length-1) {
      navigate(students[index+1]._id, {replace: true});
    }
  }

  const shouldBlock = useShouldBlock({type: "forms"});
  const [openErrorModal] = useErrorModal();
  const [openReturnSubmissionsModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitModal() {
      setLoading(true);
  
      returnSubmissions({ assessment_id: grader.assessment._id, course_id: grader.assessment.course_id, student_uids: context.student_uids }).then(function(data) {
        closeModal();
        navigate(selected_student_id || ".", { replace: true });
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    if (shouldBlock()) {
      return (
        <Modal title={"You have unsaved changes"}>
          <ModalContent>
            {"Save your changes before returning this submission."}
          </ModalContent>
          <ModalButtons>
            <Button text="Got it" className="" onClick={closeModal}/>
          </ModalButtons>
        </Modal>
      )
    }

    return (
      <Modal title={context.type == "single" ? "Return this submission?" : `Return all${context.student_uids.length > 1 ? ` ${context.student_uids.length}` : ''} completed submissions?`}>
        <ModalContent>
          {`The student${context.type == "single" ? '' : 's'} will be notified that new feedback is available.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Return" className="primary" onClick={submitModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const { handleOnClick } = useMenu({
    menuItems: [
      {
        label: 'Return this submission',
        onClick: () => {openReturnSubmissionsModal({type: "single", student_uids: [selected_student._id]})},
        disabled: !selected_student?.session?.submitted || selected_student?.session?.returned,
      },
      {
        label: 'Return all completed submissions',
        onClick: () => {openReturnSubmissionsModal({type: "all_completed", student_uids: students.filter(a => a.session?.submitted && !a.session?.returned).map(a => a._id)})},
        disabled: students.filter(a => a.session?.submitted && !a.session?.returned).length == 0,
      }
    ]
  });

  const hide_stats = useBreakpoint((width) => width <= 900);

  const wrap_student_controls = useBreakpoint((width) => width <= 660);

  const student_controls = (
    <div className="grader_student_controls">
      <CircularMenuButton onClick={() => {continueUnlessBlocked(handlePrevious)}} disabled={!hasPrevious} icon="chevron-left"/>
      <GenericDropdown items={items} menuPaddingHeight={10} value={selected_student_id} default_element={<PlaceholderDropdownItem/>} onChange={handleChange} className="grader_student_dropdown"/>
      <CircularMenuButton onClick={() => {continueUnlessBlocked(handleNext)}} disabled={!hasNext} icon="chevron-right"/>
    </div>
  )

  return (
    <div className="dashboard_container">
      <header className="dashboard_topbar">
        <div className="topbar_left">
          <CircularMenuButton onClick={() => navigate(-1)} icon="arrow-left"/>
          <div className="topbar_title">{assessment.data.title}</div>
        </div>
        <div className="topbar_right">
          {!hide_stats && <div className="grader_stats">
            {/* {
              (grader.total_cost != null && false) ? (
                <>
                  <div>
                    <div>{formatCost(grader.input_cost)}</div>
                    <div>Input cost</div>
                  </div>
                  <div>
                    <div>{formatCost(grader.output_cost)}</div>
                    <div>Output cost</div>
                  </div>
                  <div>
                    <div>{formatCost(grader.total_cost)}</div>
                    <div>Total cost</div>
                  </div>
                </>
              ) : null
            } */}
            <div>
              <div>{(students.filter(a => !a.session?.submitted).length)}</div>
              <div>Assigned</div>
            </div>
            <div>
              <div>{(students.filter(a => a.session?.submitted && !a.session?.returned).length)}</div>
              <div>Completed</div>
            </div>
            <div>
              <div>{(students.filter(a => a.session?.submitted && a.session?.returned).length)}</div>
              <div>Returned</div>
            </div>
          </div>}
          <div className="topbar_button">
            <ComboButton>
              <Button text="Return" className="primary" disabled={!selected_student?.session?.submitted || selected_student?.session?.returned} onClick={() => {openReturnSubmissionsModal({type: "single", student_uids: [selected_student._id]})}}/>
              <Button icon="menu-down-enlarged" className="primary" onClick={handleOnClick}/>
            </ComboButton>
          </div>
          {!wrap_student_controls && student_controls}
        </div>
      </header>
      {wrap_student_controls && (
        <header className="dashboard_secondary_topbar">
          {student_controls}
        </header>
      )}
      <div className="dashboard_main">
        {
          params.student_uid ? (
            selected_student_id ? <Outlet key={selected_student_id} /> : <GenericEmptyListPlaceholder text={"This student isn't enrolled in the course anymore."}/>
          ) : (
            students.length > 0 ? <Navigate to={students[0]._id} replace /> : <GenericEmptyListPlaceholder text={"There aren't any students enrolled in the course yet."}/>
          )
        }
      </div>
    </div>
  )
}

export function ReviewLayoutFrame({from}) {
  const navigate = useNavigate();
  const review = useLoaderData().data.value;
  const assessment = review.assessment;
  const session = review.session;

  if (from == "session" && !session.preview) {
    return <Navigate to={"/courses/"+session.course_id+"/assessments/"+session.assessment_id+"/review"} replace />
  }

  return (
    <div className="dashboard_container">
      <header className="dashboard_topbar">
        <div className="topbar_left">
          <CircularMenuButton onClick={() => navigate(session.demo ? -2 : -1)} icon="arrow-left"/>
          <div className="topbar_title">{assessment ? assessment.data.title : session.scenario_data.title}</div>
        </div>
        <div className="topbar_right">
        </div>
      </header>
      <div className="dashboard_main">
        {
          (session?.submitted || session?.preview) ? <ReviewSubmissionLayout review={!session.preview ? true : false} due={assessment ? assessment.data.due_date : null} demo={session.demo} /> : <GenericEmptyListPlaceholder text={"You haven't submitted this assessment yet."}/>
        }
      </div>
    </div>
  )
}

export function GraderLayoutSubmission() {
  const params = useParams();
  const grader = useRouteLoaderData("grader").data.value;
  const students = grader.students;
  const privacyMode = usePrivacyMode();

  const selected_student = students.find(a => a._id == params.student_uid);

  const submission = useLoaderData().data.value;

  if (!submission?.session) {
    return <GenericEmptyListPlaceholder text={(privacyMode ? "[Student name]" : (selected_student.name || "(No name)"))+" hasn't started the assessment yet."}/>
  } else {
    return <ReviewSubmissionLayout student_name={selected_student?.name} due={grader.assessment.data.due_date}/>;
  }
}

export function ReviewSubmissionLayout({review = false, student_name, due, demo = false}) {
  const [openErrorModal] = useErrorModal();
  const navigate = useNavigate();

  const submit = useSubmit();
  const submitting = useIsFormSubmitting();
  const is_reloading = useIsReloading();
  useHandleSubmitError();

  const is_developer = useIsDeveloper();
  const privacyMode = usePrivacyMode();

  const submission = useLoaderData().data.value;

  const session = useDatabaseSync({ query: {collection: "sessions", session_id: submission.session_id}, original_value: submission.session, single_document_mode: true });

  const conversation_messages = useDatabaseSync({ query: {collection: "messages", session_id: submission.session_id}, original_value: submission.session.messages, enabled: !submission.session.concluded });

  const scenario_data = session.scenario_data;

  const feedback_tab = (review ? session.returned : session.submitted);

  const [activeTab, setActiveTab] = useState(feedback_tab ? "feedback": "transcript");

  const conversation_message_items = [{type: "opening_blurb", opening_blurb: scenario_data.opening_blurb, workup_to_date_summary: scenario_data.workup_to_date_summary}].concat(conversation_messages.map(a => {return {type: "message", message: (
    (privacyMode && student_name) ? {
      ...a,
      text: a?.text?.replaceAll(student_name, "[Student name]")?.replaceAll(student_name.split(" ")[0], "[Student name]")?.replaceAll(student_name.split(" ")[1], "[Student name]"),
      response_text: a?.response_text?.replaceAll(student_name, "[Student name]")?.replaceAll(student_name.split(" ")[0], "[Student name]")?.replaceAll(student_name.split(" ")[1], "[Student name]"),
    } : a
  )}}));

  // Differential add/rule out messages
  let differential_messages = [];
  (session.considering_differentials || []).forEach((differential, index) => {
    differential_messages.push({type: "add_differential", name: differential.rename_history?.[0]?.old_name || differential.name, why: differential.why, date: differential.date});
    (differential.rename_history || []).forEach((rename, index) => {
      differential_messages.push({type: "rename_differential", old_name: rename.old_name, new_name: rename.new_name, date: rename.date, why: rename.why});
    });
    if (differential.ruled_out) {
      differential_messages.push({type: "rule_out_differential", name: differential.name, why: differential.why_ruled_out, date: differential.date_ruled_out});
    }
  });
  differential_messages = differential_messages.sort((a, b) => a.date - b.date);

  //insert differential_messages into conversation_message_items at the correct locations based on date. For each differential_message, loop over conversation_message_items until we find one with a later date, and then insert the differential_message before that one. If we get to the end, insert at the end.
  differential_messages.forEach(differential_message => {
    let index = 0;
    while (index < conversation_message_items.length && (conversation_message_items[index].type !== "message" || conversation_message_items[index].message.date <= differential_message.date)) {
      index++;
    }
    conversation_message_items.splice(index, 0, differential_message);
  });

  // Group messages by thread
  const conversation_message_item_groups = [];

  let previousTo = null;
  let currentGroup = [];

  conversation_message_items.forEach(item => {
    let thisTo = item.type == "message" ? item.message.to : "patient";
    if (thisTo !== previousTo) {
      if (currentGroup.length > 0) {
        conversation_message_item_groups.push(currentGroup);
      }
      currentGroup = [];
    }
    currentGroup.push(item);
    previousTo = thisTo;
  });

  if (currentGroup.length > 0) {
    conversation_message_item_groups.push(currentGroup);
  }

  function handleRegenerateFeedback(from_error, part, do_locked_in) {
    regenerateFeedback({ session_id: submission.session_id, part: part || "both", do_locked_in: do_locked_in || false }).then(() => {
      navigate('.', { replace: true });
    }).catch((error) => {
      console.error(error);
      openErrorModal();
    });
  }

  const [openRegenerateModal] = useModal(({ closeModal }) => {
    function doRegenSubmit({ formatted_data }) {
      handleRegenerateFeedback(false, formatted_data.part, formatted_data.do_locked_in);
      closeModal();
    }

    return (
      <Modal title="Regenerate...">
        <FormProvider onSubmit={doRegenSubmit}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup name="part" default_value="both">
                    <RadioOption name="rubric" text="Just rubric"/>
                    <RadioOption name="feedback" text="Just feedback"/>
                    <RadioOption name="both" text="Both"/>
                  </RadioGroup>
                  <Spacing/>
                  <Checkbox name="do_locked_in" text="Regenerate even if prompt is public & locked in" default_value={false}/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  function submitForm({ formatted_data, updateData, passthrough_args }) {
    submit(formatted_data, { method: "post", encType: "application/json" });
  }

  let eligible_prompts = Object.entries(session.feedback_data?.prompts || {}).filter(a => a[1].enabled_publicly || is_developer).sort((a, b) => a[1].sort_order - b[1].sort_order).map(([key, a]) => [key, {
    name: !is_developer ? a.public_name : a.name,
    feedback: (privacyMode && student_name) ? a.feedback.replaceAll(student_name, "[Student name]").replaceAll(student_name.split(" ")[0], "[Student name]").replaceAll(student_name.split(" ")[1], "[Student name]") : a.feedback,
    rubric: a.rubric,
  }]);

  const [promptId, setPromptId] = useState(Object.keys(Object.fromEntries(eligible_prompts))[0]);


  const messages_scroller_ref = useRef(null);
  const scroll_lock_ref = useRef(false);
  const scroll_lock_position_ref = useRef(0);
  const previous_height_ref = useRef(0);

  useEffect(() => {
    if (messages_scroller_ref.current) {
      const container = messages_scroller_ref.current;
      const newHeight = container.scrollHeight;
      previous_height_ref.current = newHeight;

      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 91;

      if (atBottom) {
        scroll_lock_ref.current = true;
        scroll_lock_position_ref.current = container.scrollHeight - container.clientHeight;
      }
    }
  }, []);

  useEffect(() => {
    if (messages_scroller_ref.current && scroll_lock_ref.current) {
      const container = messages_scroller_ref.current;
      const newHeight = container.scrollHeight;
      const heightDiff = newHeight - previous_height_ref.current;

      const whereWeShouldAlreadyBe = newHeight - container.clientHeight - heightDiff;

      container.scrollTo({
        top: whereWeShouldAlreadyBe + Math.min(heightDiff, container.clientHeight - 91),
        behavior: 'smooth'
      });
      
      previous_height_ref.current = newHeight;
    }
  }, [conversation_message_items.length]);

  function handleMessagesScroll(event) {
    const container = event.target;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 91;
    
    if (atBottom) {
      scroll_lock_ref.current = true;
      scroll_lock_position_ref.current = container.scrollHeight - container.clientHeight;
    } else if (container.scrollTop < scroll_lock_position_ref.current) {
      scroll_lock_ref.current = false;
      scroll_lock_position_ref.current = 0;
    }
  }

  const is_mobile = useIsMobile();

  return (
    <>
      <Scrollable className="dashboard_content" depencencies={[activeTab, session.ai_feedback_status?.status]}>
        <Header tabs={[
            ...(feedback_tab ? [{id: "feedback", name: "Feedback"}] : []),
            {id: "transcript", name: "Transcript"},
            ...((session.concluded && session.data && scenario_data.diagnose) ? [{id: "diagnoses", name: "Diagnoses"}] : []),
            ...((session.concluded && session.data && scenario_data.assessment_and_plan) ? [{id: "assessment_and_plan", name: "Assessment and Plan"}] : []),
            ...((session.concluded && session.data && scenario_data.questions?.length > 0) ? [{id: "questions", name: "Follow-up questions"}] : []),
            ...(is_mobile ? [{id: "submission_details", name: "Submission details"}] : [])
          ]} activeTab={activeTab} setActiveTab={setActiveTab}></Header>
        <div style={{display: "contents"}} id="grader_feedback">
          <FormProvider initial_data={Object.fromEntries(eligible_prompts)} key={session.ai_feedback_status?.status+"_"+is_developer} onSubmit={submitForm} unsaved_modal="review">
            <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "feedback"} style={{display: (activeTab == "feedback" ? "" : "none")}}>
              <div className="dashboard_content_container">
                {
                  session.ai_feedback_status?.status == "completed" ? (
                    <>
                      <div className="callout_hint_box">
                        <div>
                          <div><Icon name="sparkles"/></div>
                          <div>This feedback was generated by AI, which can sometimes make mistakes.</div>
                        </div>
                        <div>
                          <div><Icon name="account-edit"/></div>
                          <div>Your instructor had the opportunity to review and make corrections.</div>
                        </div>
                      </div>
                      {is_developer ? <>
                        Last generated: {new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }).format(session.ai_feedback_status.date)}
                        <Spacing/>
                      </> : null}
                      {(eligible_prompts.length > 1 || is_developer) ? <>
                        <Dropdown title="Available feedback:" value={promptId} onChange={setPromptId}>
                          {eligible_prompts.map(a => {
                            return <DropdownOption name={a[0]} text={a[1].name} key={a[0]}/>
                          })}
                        </Dropdown>
                        <Spacing/>
                      </> : null}
                      {eligible_prompts.map(a => {
                        return <div style={{display: (promptId == a[0] || eligible_prompts.length == 1) ? "contents" : "none"}} key={a[0]}>
                          <FormObjectGroup name={a[0]}>
                            <Textbox name="feedback" markdown={true} fullWidth={true} readOnly={review ? true : false}/>
                            <div style={{display: is_developer ? "" : "none"}}>
                              <Spacing/>
                              <Textbox name="rubric" title="Rubric" multiline={true} fullWidth={true} height={20} readOnly={true} presentation_mode={true}/>
                            </div>
                            <div style={{display: "none"}}>
                              <Textbox name="name" title="Name"/>
                            </div>
                          </FormObjectGroup>
                         </div>
                      })}
                      {(is_mobile && demo) && <>
                        <Spacing/>
                        <InlineHeader title="Next steps"/>
                        <InlineSection>
                          <DemoCaseNextSteps/>
                        </InlineSection>
                      </>}
                    </>
                  ) : (
                    (session.ai_feedback_status?.status == "waiting" || session.ai_feedback_status?.status == "in_progress" || session.ai_feedback_status?.status == "in_progress_waiting") ? (
                      <div className="error_box">
                        <div>
                          <div className="sparkles_animation"><Lottie animationData={sparklesAnimation} /></div>
                          <div className="text_ai_shimmer">Generating personalized feedback...</div>
                        </div>
                      </div>
                    ) : (
                      (session.ai_feedback_status?.status == "error") ? (
                        <div className="error_box">
                          <div>
                            <div><Icon name="alert-circle-outline"/></div>
                            <div>Something went wrong</div>
                            <div>
                              <Button icon="refresh" text="Regenerate" onClick={() => {handleRegenerateFeedback(true)}}/>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="error_box">
                          <div>
                            <div><Icon name="comment-text-outline"/></div>
                            <div>Feedback hasn&apos;t been generated yet.</div>
                            <div>
                              <Button icon="sparkles" className="primary" text="Generate" onClick={() => {handleRegenerateFeedback(true)}}/>
                            </div>
                          </div>
                        </div>
                      )
                    )
                  )
                }
              </div>
            </div>
            {(!review && session.ai_feedback_status?.status == "completed" && activeTab == "feedback") ? <>
              <Actionbar>
                <ActionbarLeft>
                  {is_developer ? <Button icon="refresh" text="Regenerate" onClick={openRegenerateModal}/> : null}
                </ActionbarLeft>
                <ActionbarRight>
                  <FormSubmitProvider>
                    {({handleSubmit, has_changed}) => {
                      return <Button className="primary" text="Save changes" onClick={handleSubmit} disabled={submitting || !has_changed || is_reloading}/>
                    }}
                  </FormSubmitProvider>
                </ActionbarRight>
              </Actionbar>
            </> : null}
          </FormProvider>
        </div>
        <div className="conversation_messages_container" style={{display: (activeTab == "transcript" ? "" : "none")}}>
          <div className="conversation_messages_container_inner nearest_scrollable_container" hidden={activeTab !== "transcript"} ref={messages_scroller_ref} onScroll={handleMessagesScroll}>
            <div className="conversation_message_actions_background"></div>
            <div className="conversation_message_history">
              <MessageItemGroups conversation_message_item_groups={conversation_message_item_groups} actors={session.actors}/>
            </div>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "diagnoses"} style={{display: (activeTab == "diagnoses" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <FormProvider initial_data={session.data || {}} readOnly={true} presentation_mode={true}>
              <DiagnosesAndDifferentialsForm/>
            </FormProvider>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "assessment_and_plan"} style={{display: (activeTab == "assessment_and_plan" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <FormProvider initial_data={session.data || {}} readOnly={true} presentation_mode={true}>
              <Textbox name="treatment_plan" title="Assessment and plan" markdown={true} fullWidth={true} multiline={true}/>
            </FormProvider>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "questions"} style={{display: (activeTab == "questions" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <FormProvider initial_data={session.data || {}} readOnly={true} presentation_mode={true}>
              <TextboxGroup name="questions" textboxes={(scenario_data.questions || []).map(a => {return {title: a}})} multiline={true}/>
            </FormProvider>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "submission_details"} style={{display: (activeTab == "submission_details" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <SubmissionDetails session={session} due={due} submission={submission} conversation_message_items={conversation_message_items}/>
          </div>
        </div>
      </Scrollable>
      {!is_mobile && (!demo ? (
        <Scrollable className="dashboard_side_panel">
          <Header title="Submission details" className="mini"/>
          <div className="dashboard_side_panel_inner nearest_scrollable_container">
            <SubmissionDetails session={session} due={due} submission={submission} conversation_message_items={conversation_message_items}/>
          </div>
        </Scrollable>
      ) : (
        <Scrollable className="dashboard_side_panel">
          <Header title="Next steps" className="mini"/>
          <div className="dashboard_side_panel_inner nearest_scrollable_container">
            <DemoCaseNextSteps/>
          </div>
        </Scrollable>
      ))}
    </>
  )
}

function SubmissionDetails({ session, due, submission, conversation_message_items }) {
  const [openSubmissionHistoryModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Submission history">
        <ModalContent>
          <div className="submission_history_container">
            {session.returned ? <div>
              <div>Returned</div>
              <div>{formatDate(session.returned_date)}</div>
            </div> : null}
            {session.submitted ? <div>
              <div>{due && due < session.submitted_date ? "Completed late" : "Completed"}</div>
              <div>{formatDate(session.submitted_date)}</div>
            </div> : null}
            {session.concluded ? <div>
              <div>Concluded encounter</div>
              <div>{formatDate(session.concluded_date)}</div>
            </div> : null}
            <div>
              <div>Started</div>
              <div>{formatDate(session.date)}</div>
            </div>
          </div>
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, [session.returned, session.submitted, session.concluded])

  const now = useDate({updateWhen: "minute_change"});

  const is_developer = useIsDeveloper();

  return (
    <>
      <div className="submission_status links">
        <div><StudentCompletionIcon session={session} due={due}/></div>
        <div>
          <div>{
            session.submitted ? (
              session.returned ? (
                (due && due < session.submitted_date) ? (
                  "Returned (completed late)"
                ) : (
                  "Returned"
                )
              ) : (
                (due && due < session.submitted_date) ? (
                  "Completed late"
                ) : (
                  "Completed"
                )
              )
            ) : (
              "In progress"
            )
          }</div>
          <div>{(!session.submitted ? "Started: " : "") + formatDate(session.submitted ? (session.returned ? session.returned_date : session.submitted_date) : session.date)}</div>
          <div><a onClick={openSubmissionHistoryModal} tabIndex={0}>See history</a></div>
        </div>
      </div>
      <div className="submission_stat">
        <div><Icon name="clock-outline"/></div>
        <div>{session.submitted ? `Took ${timeTaken(session.submitted_date - session.date)}` : `Last active ${timeSince((Date.now() - session.date_last_active < 1000*60*5) ? Date.now() : session.date_last_active, now).toLowerCase()}`}</div>
      </div>
      {(session.concluded && is_developer) ? <><br/><SaveTranscriptButton conversation_message_items={conversation_message_items.filter(a => !(a.type == "message" && a.message.to !== "patient"))} session_id={submission.session_id}/></> : null}
    </>
  )
}

function DemoCaseNextSteps() {
  const navigate = useNavigate();

  return (
    <>
      <div className="demo_next_step_card">
        <div>Explore the educator tools</div>
        <div>Create a new account with your institutional email for a free trial of our scenario builder and classroom management tools.</div>
        <div className="buttons right">
          <Button text="Create account" className="primary" onClick={() => {navigate("/login")}}/>
        </div>
      </div>
      <div className="demo_next_step_card">
        <div>Schedule a guided tour</div>
        <div>Interested in learning more about Sapient Clinician for your program? Let's talk.</div>
        <div className="buttons right">
          <Button text="Schedule a guided tour" className="primary" onClick={() => {window.open("https://sapientclinician.com/contact", "_blank")}}/>
        </div>
      </div>
      <div className="demo_next_step_card">
        <div>Share this demo with colleagues</div>
        <div style={{userSelect: "text"}}>sapientclinician.com/demo</div>
        <div className="buttons right">
          <CopyTextButton text={"https://sapientclinician.com/demo"}/>
        </div>
      </div>
    </>
  )
}

function CopyTextButton({text}) {
  const [justCopied, setJustCopied] = useState(false);
  const resetCopiedTimeout = useRef(null);

  const copyText = function() {
    navigator.clipboard.writeText(text);
    setJustCopied(true);

    if (resetCopiedTimeout.current) {
      clearTimeout(resetCopiedTimeout.current);
    }

    resetCopiedTimeout.current = setTimeout(() => {
      setJustCopied(false);
    }, 1500);
  }

  return <Button text={justCopied ? "Copied" : "Copy link"} icon={justCopied ? "check" : "copy"} className="hollow small" onClick={copyText}/>
}

export function MessageItemGroups({ conversation_message_item_groups, actors }) {

  let message_groups = conversation_message_item_groups.map((items, index) => {

    let to = items[0].type == "message" ? items[0].message.to : "patient";
    let message_items = items.map((item, index) => <MessageItem key={index} item={item} review={true} color={to != "patient" ? "advice" : null}/>);

    if (to == "patient") {
      return (
        <div key={index}>
          {message_items}
        </div>
      )
    } else {
      return (
        <div key={index} className="message_group color_advice">
          <div className="message_group_header">Advice consult with <span>{actors.find(a => a.id == to).name}</span></div>
          <div>
            {message_items}
          </div>
        </div>
      )
    }
  })

  return message_groups;

}